import axios from "axios";
import { getToken, isTokenExpired, logout } from "./auth";

// Usa URL relativi per sfruttare il proxy di React
const api = axios.create({
  baseURL: '' // Lascia vuoto per usare il proxy di React
});

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    console.log('API request interceptor:', {
      url: config.url,
      hasToken: !!token,
      tokenExpired: token ? isTokenExpired() : 'No token'
    });
    
    if (token && !isTokenExpired()) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (token && isTokenExpired()) {
      console.warn('Token expired, removing from storage');
      logout();
      // Redirect to login if we're not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API response error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url
    });
    
    if (error.response?.status === 401) {
      console.warn('Unauthorized response, redirecting to login');
      logout();
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Analytics API methods
export const analyticsApi = {
  async getAnalyticsSummary(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/overview${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics overview:', error);
      throw error;
    }
  },

  async getChatLogs({ query = '', platform = '', sentiment = '', start_date = '', end_date = '', page = 1, page_size = 20 } = {}) {
    try {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (platform) params.append('platform', platform);
      if (sentiment) params.append('sentiment', sentiment);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      params.append('page', page);
      params.append('page_size', page_size);
      const response = await api.get(`/analytics/conversations/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat logs:', error);
      throw error;
    }
  },

  async getConversationMessages(conversationId, page = 1, page_size = 20) {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', page_size);
      const response = await api.get(`/analytics/conversations/${conversationId}/messages?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      throw error;
    }
  },

  async getSentimentAnalysis(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/sentiment-analysis${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sentiment analysis:', error);
      throw error;
    }
  },

  async getUserEngagement(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/user-engagement${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user engagement:', error);
      throw error;
    }
  },

  async getPlatformStats(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/users${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching platform stats:', error);
      throw error;
    }
  },

  async getAIMetrics(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/ai-metrics${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching AI metrics:', error);
      throw error;
    }
  },

  async getRealtimeStats(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/realtime-stats${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching realtime stats:', error);
      throw error;
    }
  },

  async exportConversations(format = 'csv', params = {}) {
    try {
      // Filter out undefined/null/empty values
      const cleanParams = Object.entries(params)
        .filter(([key, value]) => value !== undefined && value !== null && value !== '')
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
      
      const search = new URLSearchParams({...cleanParams, format}).toString();
      const response = await api.get(`/analytics/export/conversations?${search}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conversations_export_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error exporting conversations:', error);
      throw error;
    }
  },

  async closeConversation(conversationId, reason = '') {
    try {
      const response = await api.post(`/analytics/conversations/${conversationId}/close`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error closing conversation:', error);
      throw error;
    }
  },

  async generateReport(reportType = 'summary', params = {}) {
    try {
      const response = await api.post('/analytics/generate-report', {
        report_type: reportType,
        ...params
      });
      return response.data;
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  },

  async getUsers(params = {}) {
    try {
      const search = new URLSearchParams(params).toString();
      const response = await api.get(`/analytics/users${search ? '?' + search : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  async exportConversationMessages(conversationId, format = 'csv') {
    try {
      const response = await api.get(`/analytics/export/conversations/${conversationId}/messages?format=${format}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conversation_${conversationId}_messages_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error exporting conversation messages:', error);
      throw error;
    }
  },

  async generateConversationSummary(conversationId, language = 'it') {
    try {
      const response = await api.post(`/analytics/conversations/${conversationId}/generate-summary?language=${language}`);
      return response.data;
    } catch (error) {
      console.error('Error generating conversation summary:', error);
      throw error;
    }
  },

  async getIntentStatistics(days = 30) {
    try {
      const response = await api.get(`/analytics/intents?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching intent statistics:', error);
      throw error;
    }
  }
};

export { api };
export default api;
